<template>
  <div class="container">
    <el-dialog
      v-if="modal.show"
      :visible.sync="modal.show"
      width="500px"
      :fullscreen="$mq === 'xs'"
      :showClose="false">
      <div slot="title">
        <h4 slot="title" class="title title-up product-name">{{ selectedProduct.name }}</h4>
      </div>
      <p>{{$t(`Do you really want to ${isEditing ? 'change' : 'activate'} the subscription?`)}}</p>
      <p v-if="isEditing && !$route.params.starts_in_future">{{$t('Subscription will be recreated after changing options')}}</p>
      <template slot="footer">
        <el-button class="cancel-button" @click.native="toggleModal">{{ $t('No') }}</el-button>
        <el-button :loading="modal.loading" v-if="isEditing" type="success" @click.native="updateSubscription">{{ $t('Yes') }}</el-button>
        <el-button :loading="modal.loading" v-else type="success" @click.native="activateSubscription">{{ $t('Yes') }}</el-button>
      </template>
    </el-dialog>
    <el-dialog
      v-if="deleteModal.show"
      :visible.sync="deleteModal.show"
      width="500px"
      :fullscreen="$mq === 'xs'"
      :showClose="false">
      <div slot="title">
        <h4 slot="title" class="title title-up product-name">{{ selectedProduct.name }}</h4>
      </div>
      <p>{{$t(`Are you sure you want to cancel your scheduled subscription?`)}}</p>
      <template slot="footer">
        <el-button class="cancel-button" @click.native="deleteModal.show = false">{{ $t('No') }}</el-button>
        <el-button :loading="deleteModal.loading" v-if="isEditing" type="success" @click.native="deleteSubscription">{{ $t('Yes') }}</el-button>
      </template>
    </el-dialog>
    <card>
      <h4 v-if="this.$route.meta.isGuest" class="card-title">{{ $t('Product constructor') }}</h4>
      <h4 v-else class="card-title d-flex align-items-center">
        <el-button class="mr-3" @click="goBack" icon="el-icon-arrow-left" size="mini" circle></el-button>
        <span v-if="$route.params.showPlannedChanges">{{ $t('Scheduled subscription version') }}</span>
        <span v-else-if="$route.params.editing">{{ $t('Change subscription') }}</span>
        <span v-else>{{ $t('New subscription')}}</span>
      </h4>
      <div class="changes-warning" v-if="$route.params.showPlannedChanges">
        <i class="el-icon-warning-outline"></i>
        <h7>
          {{ $t('Will start from {date}', { date: formatDate($route.params.timefrom) }) }}
        </h7>
      </div>
      <div class="constructor">
        <div class="col-xl-6 p-5">
          <div v-if="this.$route.meta.isGuest ? false : agreements.length > 1" class="agreement-select">
            <span class="label">{{ $t('Agreement') }}</span>
            <!-- <span v-else>{{ $t('Choose agreement') }}</span> -->
            <el-select
              v-model="agreementIndex"
              :disabled="this.$route.params.editing"
              class="select-input"
              :placeholder="$t('Choose agreement')">
              <el-option
                v-for="(agreement, index) in agreements"
                :key="agreement.id"
                :label="agreement.number"
                :value="index">
              </el-option>
            </el-select>
          </div>
          <div class="product-select">
            <span class="label">{{ $t('Product') }}</span>
            <el-select
              v-model="productIndex"
              :disabled="this.$route.params.editing"
              :placeholder="$t('Choose product')"
              class="select-input">
              <el-option
                v-for="(product, index) in products"
                :key="product.id"
                :label="product.name"
                :value="index">
              </el-option>
            </el-select>
          </div>
          <div class="period-select">
            <span class="label">{{ $t('Period') }}</span>
            <!-- <span v-else>{{ $t('Choose period') }}</span> -->
            <el-select
              v-model="periodIndex"
              :disabled="this.$route.params.editing"
              :placeholder="$t('Choose period')"
              class="select-input">
              <el-option
                v-for="(period, index) in periods"
                :key="period.record_id"
                :label="getPeriodString(period.period_type, period.period_length)"
                :value="index">
                <span>{{getPeriodString(period.period_type, period.period_length)}}</span>
              </el-option>
            </el-select>
          </div>
          <div class="date-select" v-if="isEditing">
            <span >{{ $t('From date') }}</span>
            <el-date-picker
              class="select-input"
              v-model="timefrom"
              type="date"
              format="dd.MM.yyyy"
              :clearable="false"
              :picker-options="pickerOptions">
            </el-date-picker>
          </div>
          <collapse v-if="selectedProduct.descr || selectedProduct.link" class="subscription-description">
            <collapse-item name="1">
              <template slot="title">
                <i class="header-icon el-icon-info"></i> {{$t('Subscription info')}}
              </template>
              <div>
                <p v-if="selectedProduct.descr" :class="{ 'mb-0': !selectedProduct.link }" v-html="selectedProduct.descr"></p>
                <a v-if="selectedProduct.link" :href="selectedProduct.link" target="_blank">{{ $t('More') }}</a>
              </div>
            </collapse-item>
          </collapse>
        </div>
        <div v-loading="loading" class="col-xl-6 p-5">
          <div v-if="optionsBuild.length === 0"><el-empty class="empty-services" :description="$t('Services are not available')" :image-size="100"></el-empty></div>
          <ul v-else class="options-list">
            <li
              is="subscription-option"
              v-for="option in optionsBuild"
              :key="option.servCatIdx + '_' + option.tarid"
              :option="option"
              :currency="selectedProduct.currency"
              :timefrom="timefrom"
              v-on:updateProductBuild="updateProductBuild">
            </li>
          </ul>
          <div v-if="!loading && optionsBuild.length > 0" class="activate-product">
              <div class="price">
                <div class="monthly" v-if="totalPrice.monthly">
                  {{ `${totalPrice.monthly} ${this.selectedProduct.currency} ${this.$t('for')} ${$t('Period').toLowerCase()}` }}
                </div>
                <div class="once" v-if="totalPrice.once">
                  {{ `${totalPrice.once} ${this.selectedProduct.currency} ${$t('once')}` }}
                </div>
              </div>
              <div class="buttons">
                <el-button
                  type="primary"
                  round
                  @click.prevent="toggleModal"
                >
                  {{ buttonText }}
                </el-button>
                <el-button
                  slot="reference"
                  type="text"
                  v-if="$route.params.showPlannedChanges"
                  style="color: #f56c6c"
                  @click.prevent="deleteModal.show = !deleteModal.show"
                >
                  {{this.$t('Remove scheduled version')}}
                </el-button>
                <!-- <el-button
                  type="text"
                  v-if="this.$route.params.editing"
                  @click.prevent="goBack"
                  style="color: #909399"
                >
                  {{this.$t('To cancel')}}
                </el-button> -->
              </div>
          </div>
        </div>
      </div>
    </card>
  </div>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'
import { Select, Option, Collapse, CollapseItem, Button, DatePicker, Empty } from 'element-ui'
import { findIndex } from 'lodash'
import SubscriptionsConstructorOption from './SubscriptionsConstructorOption'
import { Decimal } from 'decimal.js'
import { format } from 'date-fns'
import { productOption } from './mixins/productOption.js'
import { chargingPeriods } from './mixins/chargingPeriods'

export default {
  name: 'SubscriptionsConstructor',
  mixins: [productOption, chargingPeriods],
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
    [Empty.name]: Empty,
    [SubscriptionsConstructorOption.name]: SubscriptionsConstructorOption,
    'el-button': Button,
    Collapse,
    CollapseItem
  },
  data() {
    let data = {
      products: null,
      periods: null,
      productIndex: null,
      periodIndex: null,
      agreementIndex: 0,
      optionsBuild: [],
      loading: true,
      timefrom: null,
      previousRoute: null,
      modal: {
        show: false,
        loading: false
      },
      deleteModal: {
        show: false,
        loading: false
      }
    }
    // const today = new Date()

    data.timefrom = this.$route.params.timefrom
    // if (this.$route.params.starts_today) {
    //   data.timefrom = addDays(today, 1)
    // } else {
    //   data.timefrom = new Date()
    // }
    // let fromLimit = subDays(data.timefrom, 1)
    // if (this.$route.params.showPlannedChanges) {
    //   fromLimit = subDays(new Date(), 1)
    // }
    let timetoDate = Infinity
    if (!this.$route.params.showPlannedChanges && this.$route.params.timeto && this.$route.params.timeto !== '31.12.9999') {
      const timeto = this.$route.params.timeto.split('.') || Infinity
      timetoDate = new Date(timeto[2], timeto[1] - 1, timeto[0])
    }
    data.pickerOptions = {
      disabledDate: (time) => {
        return time.getTime() < this.$route.params.minTimefrom.getTime() || time.getTime() > timetoDate
      },
      firstDayOfWeek: 1
    }

    return data
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.previousRoute = from.path
    })
  },
  computed: {
    hasTimeto: function() {
      return this.$route.params.timeto && this.$route.params.timeto !== '31.12.9999'
    },
    totalPrice: function () {
      const totalPrice = {
        'once': 0,
        'monthly': 0
      }
      this.optionsBuild.every(option => {
        if (!option.switchedOn || option.bulkService) {
          return true
        }
        if (option.period === 0) {
          totalPrice.once = Decimal(totalPrice.once).plus(Decimal.mul(this.priceWithDiscounts(option, this.timefrom), option.defaultMul))
        } else {
          totalPrice.monthly = Decimal(totalPrice.monthly).plus(Decimal.mul(this.priceWithDiscounts(option, this.timefrom), option.defaultMul))
          totalPrice.once = Decimal(totalPrice.once).plus(Decimal.mul(this.priceWithDiscounts(option, this.timefrom, true), option.defaultMul))
        }
        return true
      })

      totalPrice.once = parseFloat(totalPrice.once)
      totalPrice.monthly = parseFloat(totalPrice.monthly)

      return totalPrice
    },
    selectedAgreement: function() {
      return this.agreements[this.agreementIndex]
    },
    selectedProduct: function() {
      return this.products[this.productIndex]
    },
    selectedPeriod: function() {
      return this.periods[this.periodIndex]
    },
    ...mapState({
      agreements: state => state.profile.agreements
    }),
    buttonText: function() {
      if (this.$route.params.editing) {
        return this.$t('Save')
      } else if (this.$route.meta.isGuest) {
        return this.$t('Sign Up')
      } else {
        return this.$t('Create')
      }
    },
    isEditing: function() {
      return this.$route.params.editing || false
    }
  },
  watch: {
    productIndex: function(val) {
      console.log('productIndex', val)
      if (val !== null) {
        this.periodIndex = null
        // this.optionsBuild = []
        this.fetchPeriods()
      } else {
        this.loading = false
      }
    },
    periodIndex: function(val) {
      if (val !== null) {
        this.fetchProduct()
      } else {
        this.loading = false
      }
    }
  },
  mounted() {
    if (this.agreements.length || this.$route.meta.isGuest) {
      this.fetchProducts()
    } else {
      this.$store.dispatch('getProfile').then(promise => {
        this.fetchProducts()
      })
    }
  },
  methods: {
    fetchProducts() {
      console.log('fetchProducts')
      axios.get('products').then(promise => {
        this.products = promise.data.results
        this.agreementIndex = this.$route.params.agrmid ? findIndex(this.agreements, { id: this.$route.params.agrmid }) : 0
        if (this.isEditing) {
          this.productIndex = findIndex(this.products, { id: this.$route.params.productId })
        } else {
          this.productIndex = 0
        }
      }).catch(error => {
        console.error(error.response.data)
      })
    },
    fetchPeriods() {
      console.log('fetchPeriods')
      console.log('this.$route.params', this.$route.params)
      const tarId = this.$route.params.editing ? this.$route.params.productId : this.selectedProduct.id
      console.log('tarId', tarId)
      axios.get('periods', {
        params: {
          tarId: tarId
        }
      }).then(promise => {
        this.periods = promise.data.results
        console.log('promise.data.results', promise.data.results)
        if (this.isEditing) {
          this.periodIndex = findIndex(this.periods, { period_id: this.$route.params.periodId })
        } else if (promise.data.results.length) {
          this.periodIndex = 0
        } else {
          this.loading = false
        }
      }).catch(error => {
        this.loading = false
        console.error(error.response.data)
      })
    },
    fetchProduct() {
      console.log('fetchProduct')
      this.loading = true
      const productId = this.$route.params.editing ? this.$route.params.productId : this.selectedProduct.id
      const periodId = this.$route.params.editing ? this.$route.params.periodId : this.selectedPeriod.period_id
      const queryParams = {}
      if (this.$route.meta.isGuest) {
        queryParams.periodId = periodId
      } else if (this.$route.params.editing) {
        queryParams.agrmid = this.$route.params.agrmid
        queryParams.periodId = periodId
        queryParams.subscriptionId = this.$route.params.subscriptionId
        if (this.$route.params.showPlannedChanges) {
          queryParams.globalId = this.$route.params.globalId
        }
      } else {
        queryParams.agrmid = this.selectedAgreement.id
        queryParams.periodId = periodId
      }
      this.loading = true
      axios.get('products/' + productId, {
        params: queryParams
      }).then(promise => {
        console.log('result', promise.data.results)
        this.optionsBuild = promise.data.results
        this.loading = false
      }).catch(error => {
        console.log(error)
        this.loading = false
      })
    },
    activateSubscription: function() {
      this.modal.loading = true
      axios.post('subscriptions/create', {
        productId: this.selectedProduct.id,
        agrmid: this.selectedAgreement.id,
        periodId: this.selectedPeriod.period_id,
        options: this.optionsBuild
      }).then(promise => {
        this.modal.loading = false
        this.modal.show = false
        this.$notify(
          {
            message: this.$t('Subscription activated'),
            horizontalAlign: 'center',
            verticalAlign: 'top',
            type: 'success',
            timeout: 1500
          })
        this.goBack()
      }).catch(error => {
        console.error(error.response.data)
        this.modal.loading = false
        this.modal.show = false
        this.$notify(
          {
            message: this.$t('An error occurred while activating subscription. Please try later.'),
            horizontalAlign: 'center',
            verticalAlign: 'top',
            type: 'danger',
            timeout: 1500
          })
      })
    },
    updateSubscription: function() {
      this.modal.loading = true
      let timefrom = this.timefrom
      if (this.timefrom) {
        timefrom = timefrom.getFullYear() + '-' + ('0' + (timefrom.getMonth() + 1)).slice(-2) + '-' + ('0' + timefrom.getDate()).slice(-2)
      }
      axios.put('subscriptions/' + this.$route.params.subscriptionId, {
        productId: this.selectedProduct.id,
        agrmid: this.selectedAgreement.id,
        timefrom: timefrom,
        starts_in_future: this.$route.params.starts_in_future,
        options: this.optionsBuild,
        isPlanned: this.$route.params.showPlannedChanges,
        globalId: this.$route.params.showPlannedChanges || this.$route.params.starts_in_future ? null : this.$route.params.globalId
      }).then(promise => {
        this.modal.loading = false
        this.modal.show = false
        this.$notify(
          {
            message: this.$t('Subscription changed'),
            horizontalAlign: 'center',
            verticalAlign: 'top',
            type: 'success',
            timeout: 1500
          })
        this.$router.push('/subscriptions')
      }).catch(error => {
        console.error(error.response.data)
        this.modal.loading = false
        this.modal.show = false
        this.$notify(
          {
            message: this.$t('An error occurred while activating subscription. Please try later.'),
            horizontalAlign: 'center',
            verticalAlign: 'top',
            type: 'danger',
            timeout: 1500
          })
      })
    },
    deleteSubscription: function() {
      this.modal.loading = true

      axios.delete('subscriptions/' + this.$route.params.subscriptionId, {
        data: {
          planned: true
        }
      }).then(promise => {
        this.deleteModal.loading = false
        this.deleteModal.show = false
        this.$notify(
          {
            message: this.$t('Subscription deleted'),
            horizontalAlign: 'center',
            verticalAlign: 'top',
            type: 'success',
            timeout: 1500
          })
        this.goBack()
      }).catch(error => {
        console.error(error.response.data)
        this.deleteModal.loading = false
        this.deleteModal.show = false
        this.$notify(
          {
            message: this.$t('An error occurred while activating subscription. Please try later.'),
            horizontalAlign: 'center',
            verticalAlign: 'top',
            type: 'danger',
            timeout: 1500
          })
      })
    },
    cancelEditing: function() {
      this.$router.go(-1)
    },
    formatDate: function(date) {
      console.log('timefrom', date)
      return format(new Date(date), 'dd.MM.yyyy')
    },
    toggleModal: function() {
      this.$route.meta.isGuest ? this.redirectRegister() : this.modal.show = !this.modal.show
    },
    updateProductBuild: function(build) {
      const optionIndex = findIndex(this.optionsBuild, { servCatIdx: build.servCatIdx })
      this.optionsBuild[optionIndex].defaultMul = build.mul
      this.optionsBuild[optionIndex].switchedOn = build.switchedOn
    },
    redirectRegister: function() {
      this.$router.push('/register')
    },
    goBack: function() {
      this.$router.push(this.previousRoute || '/dashboard')
    }
  }
}
</script>

<style lang="scss" scoped>
.constructor {
  display: flex;
  > *:not(:last-child) {
    border-right: 1px solid lightgray;
    @media (max-width: 1199px) {
      border-right: none;
      border-bottom: 1px solid lightgray;
    }
  }
  @media (max-width: 1199px) {
    flex-direction: column;
  }
}
.base-params,
.options-constructor {
  padding: 50px;
}

.product-name {
  color: inherit;
  margin-top: 0;
  margin-bottom: 0;
}

.product-select,
.agreement-select,
.date-select,
.period-select {
  & > span {
    min-width: 60px;
  }
  display: flex;
  align-items: center;
  &:not(:last-child) {
    margin-bottom: 15px;
  }
}
.select-input {
  flex-grow: 1;
  margin-left: 20px;
}
.activate-product {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-top: 60px;
  > .price {
    font-size: 20px;
    > div {
      line-height: 1;
      &:not(:last-child) {
        margin-bottom: 8px;
      }
    }
  }

  @media (max-width: 575px) {
    flex-direction: column;
    align-items: center;
    > .price {
      margin-bottom: 30px;
    }
  }
}
.buttons {
  display: flex;
  flex-direction: column;
  > button {
    margin: 0
  }

}
.options-list {
  list-style-type: none;
  padding: 0;
  margin: 0 auto 0 auto;
}
.card-title {
  padding: 0 48px;
}
.subscription-description {
  margin-top: 40px;
  p {
    white-space: pre-line;
  }
}
.header-icon {
  color: #9A9A9A;
}
.changes-warning {
  color: #9A9A9A;
  margin-left: 92px;
}
.cancel-button {
  margin-right: 20px !important;
}
.empty-services {
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>
